import { ChatMessage } from '@/features/chat/types';
import { create } from 'zustand';

interface ChatStore {
    messages: ChatMessage[];
    isStreaming: boolean;
    streamingMessageId: string | null;
    currentConversationId: string | null;
    isMessagesLoading: boolean;
    addMessage: (message: ChatMessage) => void;
    startStreamingMessage: (messageId: string) => void;
    appendToStreamingMessage: (content: string) => void;
    finishStreamingMessage: () => void;
    clearMessages: () => void;
    isEmpty: () => boolean;
    popLastMessage: () => void;
    getLastUserMessage: () => ChatMessage | undefined;
    updateMessageId: (messageId: string, aiMessageId: string) => void;
    setCurrentConversationId: (conversationId: string) => void;
    setMessages: (messages: ChatMessage[]) => void;
    setMessagesLoading: (isLoading: boolean) => void;
}

export const useChatStore = create<ChatStore>((set, get) => ({
    messages: [],
    isStreaming: false,
    streamingMessageId: null,
    currentConversationId: null,
    conversations: [],
    isConversationsListDirty: new Date(),
    isMessagesLoading: false,
    setCurrentConversationId: (conversationId: string) => set({ currentConversationId: conversationId }),
    setMessages: (messages: ChatMessage[]) => set({ messages }),
    setMessagesLoading: (isLoading: boolean) => {
        set({ isMessagesLoading: isLoading });
    },
    updateMessageId: (messageId: string, aiMessageId: string) =>
        set(state => ({
            messages: state.messages.map(message =>
                message.id === messageId ? { ...message, id: aiMessageId } : message
            ),
        })),

    addMessage: (message: ChatMessage) =>
        set(state => ({
            messages: [...state.messages, message],
        })),

    startStreamingMessage: (messageId: string) => {
        const newMessage: ChatMessage = {
            role: 'assistant',
            content: '',
            isStreaming: true,
            id: messageId,
        };
        set(state => ({
            messages: [...state.messages, newMessage],
            isStreaming: true,
            streamingMessageId: messageId,
        }));
    },

    appendToStreamingMessage: (content: string) => {
        set(state => ({
            messages: state.messages.map(message =>
                message.id === state.streamingMessageId ? { ...message, content: message.content + content } : message
            ),
        }));
    },

    finishStreamingMessage: () => {
        set(state => ({
            messages: state.messages.map(message =>
                message.id === state.streamingMessageId ? { ...message, isStreaming: false } : message
            ),
            isStreaming: false,
            streamingMessageId: null,
        }));
    },

    clearMessages: () =>
        set({
            messages: [],
            isStreaming: false,
            streamingMessageId: null,
        }),

    isEmpty: () => {
        const { messages } = get();
        return messages.length === 0;
    },

    popLastMessage: () => {
        set(state => ({
            messages: state.messages.slice(0, -1),
        }));
    },
    getLastUserMessage: () => {
        const { messages } = get();
        // user message will be the second to last message
        return messages.length > 0 ? messages[messages.length - 2] : undefined;
    },
}));
